/**
 * Off-page badge / count-bump (PEL-516).
 *
 * A low-interruption signal pinned to the corner of an anchor (e.g. a sidebar
 * nav item) for event signals that must survive navigation. Used when an
 * event-gated step fires but a coachmark isn't appropriate (anchor off-screen
 * or a timeout fallback). Clicking acknowledges it (clears via onComplete);
 * a primaryCta with `navigate` routes the user instead.
 */

import { cn } from '@/lib/utils'
import { useRef } from 'react'
import type { ActiveFtueStep } from '../types'
import { useAnchorRect } from './hooks'

export function FtueBadge({ active }: { active: ActiveFtueStep }) {
  const rect = useAnchorRect(active.element)
  const activeRef = useRef(active)
  activeRef.current = active

  if (!rect) return null

  const { content } = active.step
  const label = content.title ?? content.body
  const navigateCta = content.primaryCta?.action === 'navigate' ? content.primaryCta : undefined

  const handleClick = () => {
    if (navigateCta?.href) activeRef.current.onCtaNavigate(navigateCta.href)
    else activeRef.current.onComplete()
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      aria-label={label}
      className={cn(
        'fixed z-[60] flex h-4 w-4 items-center justify-center rounded-full',
        'bg-blue-600 ring-2 ring-background motion-safe:animate-pulse',
      )}
      style={{ top: rect.top - 5, left: rect.left + rect.width - 11 }}
    />
  )
}
